import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { configureForm } from '@thinkmill/pragmatic-forms';
import authManager, { AUTH_STATE_CHANGE } from '../../lib/authManager';
import { css } from 'glamor';
import { Container } from '../../components/Container';
import { Input } from '../../components/Input';
import { CheckboxInput } from '../../components/CheckboxInput';
import { Button } from '../../components/Button';
import { LogoBox } from '../../components/LogoBox';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { makeStyles } from "@material-ui/core/styles";
import { Redirect } from "react-router-dom";
import { ValidationErrorMessage } from "../../components/ErrorMessage";
import RequestError from '../../lib/RequestError';
import * as theme from '../../theme';

const formLayout = css({
    display: 'flex',
    flexDirection: 'column',
});

const mfaForm = css({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
});

const mfaActions = css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    marginTop: 24,
});

const trustBrowserLabel = css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 16,
    cursor: 'pointer',
});

const useStyles = makeStyles(() => ({
    card: {
        width: '650px',
        height: '620px',
        borderRadius: '25px',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'linear-gradient(#B0FF33, #97C93B)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 6px 10px rgba(0,0,0,.08), 0 0 6px rgba(0,0,0,.05)',
        transition: '.3s transform cubic-bezier(.155,1.105,.295,1.12),.3s box-shadow',
    },
    nestedCard: {
        width: '621px',
        height: '591px',
        borderRadius: '25px',
        background: '#fff',
    },
    cardContent: {
        alignItems: 'center',
    },
}));

function Login({ form }) {
    const classes = useStyles();

    const [isAuthenticated, setIsAuthenticated] = useState(authManager.isAuthenticated);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [challenge, setChallenge] = useState(null);
    const [code, setCode] = useState('');
    const [trustBrowser, setTrustBrowser] = useState(false);
    const [expiresAt, setExpiresAt] = useState(null);
    const [now, setNow] = useState(Date.now());
    const mounted = useRef(false);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        if (!challenge) return undefined;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [challenge]);

    useEffect(() => {
        const onAuthStateChange = () => setIsAuthenticated(authManager.isAuthenticated);
        authManager.addListener("AUTH_STATE_CHANGE", onAuthStateChange);

        return () => authManager.removeListener("AUTH_STATE_CHANGE", onAuthStateChange);
    }, []);

    if (isAuthenticated) return <Redirect to="/installations" />;

    const handleSubmit = async ({ email, password }) => {
        const newErrors = {};

        if (!email?.value) newErrors.email = 'Please enter your email address';
        if (!password?.value) newErrors.password = 'Please enter your password';

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        try {
            setLoading(true);
            const result = await authManager.login({
                email: email.value,
                password: password.value,
            });
            if (mounted.current && result?.mfaRequired) { setChallenge(result.challenge); setExpiresAt(Date.now() + 10 * 60 * 1000); }
        } catch (e) {
            console.error('Login error:', e);

            const message = e instanceof RequestError
                ? e.message
                : 'Invalid email or password. Please try again.';

            if (mounted.current) setErrors({ submit: message  });
        } finally {
            if (mounted.current) setLoading(false);
        }
    };

    const verifyCode = async () => {
        setLoading(true);
        setErrors({});
        try { await authManager.verifyMfa({ challenge, code, trustBrowser }); }
        catch (e) { if (mounted.current) setErrors({ submit: e.message || 'Invalid or expired code' }); }
        finally { if (mounted.current) setLoading(false); }
    };

    const resendCode = async () => {
        setLoading(true);
        try {
            const { data } = await (await import('../../lib/api')).default.resendMfa({ challenge });
            if (mounted.current) {
                setChallenge(data.challenge);
                setExpiresAt(Date.now() + 10 * 60 * 1000);
                setErrors({});
            }
        } catch (e) { if (mounted.current) setErrors({ submit: e.message || 'Sign in again to request a new code.' }); }
        finally { if (mounted.current) setLoading(false); }
    };

    return (
      <div>
          <Card className={classes.card}>
              <Card className={classes.nestedCard}>
                  <Container size="small">
                      <CardContent className={classes.cardContent}>
                          <Helmet><title>Login</title></Helmet>

                          <div className={css({ paddingTop: 40, paddingBottom: 40 })}>
                              <LogoBox size={80} src={'/DRIVO_Logo.png'} />
                          </div>

                        {challenge ? <form className={mfaForm} onSubmit={(e) => {
                            e.preventDefault();
                            if (!loading && code.length === 6) verifyCode();
                        }}>
                            <h2 className={css({ margin: '0 0 12px', fontSize: 24, color: theme.gray80 })}>Check your email</h2>
                            <p className={css({ margin: '0 0 24px', fontSize: 16, lineHeight: 1.5, color: theme.gray60 })}>{expiresAt && now >= expiresAt ? 'Your code has expired. Request a new code.' : 'Enter the 6-digit code sent to your email. It expires in 10 minutes.'}</p>
                            {errors.submit && <ValidationErrorMessage error={{ message: errors.submit }} />}
                            <Input
                                label="Verification code"
                                aria-label="Verification code"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                                style={{ letterSpacing: '0.3em', fontVariantNumeric: 'tabular-nums' }}
                            />
                            <CheckboxInput
                                className={trustBrowserLabel}
                                checked={trustBrowser}
                                onChange={(e) => setTrustBrowser(e.target.checked)}
                            >
                                Trust this browser for 30 days
                            </CheckboxInput>
                            <div className={mfaActions}>
                                <Button type="submit" style={{ height: 45, fontSize: 18, padding: '0 20px' }} spaceBelow="none" disabled={loading || code.length !== 6}>Verify code</Button>
                                <Button type="button" mode="naked" style={{ height: 45, fontSize: 18, padding: '0 12px' }} spaceBelow="none" disabled={loading || !expiresAt || now < expiresAt} onClick={resendCode}>Resend code</Button>
                            </div>
                        </form> : <form.Form className={formLayout} onSubmit={(e) => { e.preventDefault(); handleSubmit(form.fields); }}>
                              {Object.keys(errors).length > 0 &&
                                Object.values(errors).map((value) => (
                                  <ValidationErrorMessage key={value} error={{ message: value }}/>
                                ))
                              }
                              <Input
                                {...form.getFieldProps({
                                    name: 'email',
                                    type: 'email',
                                })}
                                placeholder="Email Address"
                                autoComplete="username"
                              />
                              <Input
                                style={{ marginTop: '15px' }}
                                {...form.getFieldProps({
                                    name: 'password',
                                    type: 'password',
                                })}
                                placeholder="Password"
                                autoComplete="current-password"
                              />

                              <Button
                                type="submit"
                                onClick={() => handleSubmit(form.fields)}
                                style={{ marginTop: '20px', height: '45px', fontSize: '18px' }}
                                block
                                disabled={loading}
                              >
                                  {loading ? 'Signing in…' : 'Sign in'}
                              </Button>
                          </form.Form>}
                      </CardContent>
                  </Container>
              </Card>
          </Card>
      </div>
    );
}

export default configureForm({
    initFields: () => ({ email: '', password: '' }),
})(Login);
